import { useListChatSessions } from '@repo/service/api/tags/chat';
import type { ChatSessionDto } from '@repo/service/schemas';
import { Button, Card, Empty, Input, List, Space, Spin, Tag, Typography } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import { streamChat, type ChatStreamEvent } from '../api/chat-stream';

interface UiMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
}

export default function ChatPage() {
  /**
   * 会话列表直接用契约层生成的 hook。
   * 它自带缓存、去重与失效能力 —— 这里没有写任何 useEffect 去拉数据。
   */
  const sessionsQuery = useListChatSessions({ pageNum: 1, pageSize: 20 });

  const [activeSessionId, setActiveSessionId] = useState<string | undefined>();
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const sessions = useMemo<ChatSessionDto[]>(
    // 两层包裹要拆清楚，这是最容易踩的地方：
    //   sessionsQuery.data                 → axios 的 AxiosResponse
    //   sessionsQuery.data.data            → 业务响应包络 { code, message, data }
    //   sessionsQuery.data.data.data       → ChatSessionListDto
    //   sessionsQuery.data.data.data.items → 真正的数组
    // 类型全部来自 OpenAPI，写错一层编译器就会报——这正是契约层的价值。
    () => sessionsQuery.data?.data?.data?.items ?? [],
    [sessionsQuery.data],
  );

  const appendToLast = useCallback((delta: string, sessionId: string) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = next.at(-1);
      if (last && last.role === 'assistant' && last.pending) {
        next[next.length - 1] = { ...last, content: last.content + delta };
      } else {
        next.push({
          id: `local_${sessionId}_${next.length}`,
          role: 'assistant',
          content: delta,
          pending: true,
        });
      }
      return next;
    });
  }, []);

  const finishLast = useCallback(() => {
    setMessages((prev) =>
      prev.map((m, i) => (i === prev.length - 1 ? { ...m, pending: false } : m)),
    );
  }, []);

  const handleSend = useCallback(async () => {
    const prompt = draft.trim();
    if (prompt === '' || streaming) return;

    setDraft('');
    setError(null);
    setStreaming(true);
    setMessages((prev) => [
      ...prev,
      { id: `local_user_${prev.length}`, role: 'user', content: prompt },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    await streamChat({
      prompt,
      sessionId: activeSessionId,
      signal: controller.signal,
      onEvent: (event: ChatStreamEvent) => {
        switch (event.event) {
          case 'text':
            setActiveSessionId(event.sessionId);
            appendToLast(event.delta, event.sessionId);
            break;
          case 'done':
            setActiveSessionId(event.sessionId);
            finishLast();
            void sessionsQuery.refetch();
            break;
          case 'error':
            setError(event.errorMessage);
            finishLast();
            break;
          default:
            break;
        }
      },
    }).catch((err: unknown) => {
      // 网络层失败（如线上 Demo 未部署 API）给出人话提示，而不是浏览器原文
      const raw = err instanceof Error ? err.message : String(err);
      setError(/failed to fetch|networkerror|load failed/i.test(raw)
        ? '无法连接后端服务——在线 Demo 未包含 API，请参考项目 README 在本地启动后端后再试。'
        : raw);
    });

    setStreaming(false);
    abortRef.current = null;
  }, [activeSessionId, appendToLast, draft, finishLast, sessionsQuery, streaming]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, []);

  return (
    <div className="shell">
      <aside className="sidebar">
        <Typography.Title level={5}>会话</Typography.Title>
        {sessionsQuery.isPending && <Spin />}
        {sessionsQuery.isError && <Typography.Text type="danger">会话列表加载失败</Typography.Text>}
        {!sessionsQuery.isPending && sessions.length === 0 && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="还没有会话" />
        )}
        <List
          size="small"
          dataSource={sessions}
          renderItem={(item) => (
            <List.Item
              className={item.id === activeSessionId ? 'session active' : 'session'}
              onClick={() => setActiveSessionId(item.id)}
            >
              <div>
                <div className="session-title">{item.title}</div>
                <div className="session-meta">{item.messageCount} 条消息</div>
              </div>
            </List.Item>
          )}
        />
        <Button
          block
          onClick={() => {
            setActiveSessionId(undefined);
            setMessages([]);
            setError(null);
          }}
        >
          新建会话
        </Button>
      </aside>

      <main className="content">
        <header className="content-header">
          <Typography.Title level={4} style={{ margin: 0 }}>
            AI Console
          </Typography.Title>
          <Space>
            <Tag color={activeSessionId ? 'blue' : 'default'}>{activeSessionId ?? '新会话'}</Tag>
            <Tag>{streaming ? '生成中' : '空闲'}</Tag>
          </Space>
        </header>

        <Card className="chat-card" variant="borderless">
          {messages.length === 0 && (
            <Empty description="输入一句话开始对话（原型阶段返回模拟回复）" />
          )}
          <div className="messages">
            {messages.map((m) => (
              <div key={m.id} className={`bubble ${m.role}`}>
                <div className="bubble-role">{m.role === 'user' ? '我' : 'AI'}</div>
                <div className="bubble-body">
                  {m.content}
                  {m.pending && <span className="caret" />}
                </div>
              </div>
            ))}
          </div>
          {error !== null && <Typography.Text type="danger">出错：{error}</Typography.Text>}
        </Card>

        <div className="composer">
          <Input.TextArea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="输入内容，Enter 发送"
            autoSize={{ minRows: 2, maxRows: 6 }}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
          />
          <Space direction="vertical">
            <Button type="primary" loading={streaming} onClick={() => void handleSend()}>
              发送
            </Button>
            <Button disabled={!streaming} onClick={handleStop}>
              停止
            </Button>
          </Space>
        </div>

        <details className="debug">
          <summary>契约层调用示例（供面试讲解用）</summary>
          <pre>
            {`// 生成的请求函数与 hook 在同一个文件里（orval mode: tags-split）
import { listChatSessions, useListChatSessions } from '@repo/service/api/tags/chat';

// hook：缓存 / 去重 / 失效都由它管，页面里没有一行 useEffect
const { data } = useListChatSessions({ pageNum: 1, pageSize: 20 });
data?.data?.items;   // 类型来自 OpenAPI，不是手写的

// 需要手动调用时用请求函数
await listChatSessions({ pageNum: 2, pageSize: 20 });`}
          </pre>
        </details>
      </main>
    </div>
  );
}
